import { ApolloClient, ApolloLink, DocumentNode, FetchResult, from, HttpLink, InMemoryCache, OperationVariables } from '@apollo/client';
import { store } from 'index';
import { PUBLIC_API } from './publicApi';

const GRAPHQL_URL = process.env.REACT_APP_GRAPHQL_URL || 'http://54.254.89.223:8082/query';

const client = (url: string) => {
    // HTTP link to GraphQL server
    const httpLink = new HttpLink({ uri: url });
    // Auth middleware to add Authorization header
    const envToken = process.env.REACT_APP_PUBLIC_ACCESS_TOKEN;

    const authLink = new ApolloLink((operation, forward) => {
        // Get fresh state on each request (not at client creation time)
        const state = store.getState();
        const auth = state?.auth;
        const authUser = auth && typeof auth === 'object' && 'authState' in auth
            ? (auth as any).authState?.authUser
            : null;

        const publicAccessToken = envToken || '';
        const accessToken = authUser?.token?.access_token;
        const isPublicApi = PUBLIC_API.includes(operation.operationName);
        const token = isPublicApi ? publicAccessToken : accessToken;

        operation.setContext(({ headers = {} }) => ({
            headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : ''
            }
        }));
        return forward(operation);
    });

    return new ApolloClient({
        link: from([authLink, httpLink]), // combine links
        cache: new InMemoryCache()
    });
};

export const executeMutation = async (MUTATION: DocumentNode, INPUT: any, clienttype = 1): Promise<FetchResult<any>> => {
    try {
        const cleanInput = cleanVariables(INPUT);
        const response = await client(GRAPHQL_URL).mutate({ mutation: MUTATION, variables: cleanInput });
        return response;
    } catch (error: any) {
        if (error.graphQLErrors) {
            const formattedErrors = error.graphQLErrors.map((err: any) => ({
                message: err.message,
                locations: err.locations,
                path: err.path
            }));
            const userFriendlyMessage = formattedErrors.length > 0 ? formattedErrors[0].message : 'An unexpected error occurred';
            throw new Error(userFriendlyMessage);
        } else if (error.networkError) {
            throw new Error('Network error occurred. Please try again.');
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const executeQuery = async (QUERY: DocumentNode, INPUT?: any): Promise<FetchResult<any>> => {
    try {
        const response = await client(GRAPHQL_URL).query({ query: QUERY, variables: INPUT });
        return response;
    } catch (error: any) {
        if (error.networkError || error.graphQLErrors) {
            throw new Error('An unexpected error occurred.');
        }
        throw new Error('An unexpected error occurred.');
    }
};

// Utility: check if value is date-like
function isDateLike(value: any): boolean {
    if (value instanceof Date) return true;
    if (typeof value === 'string' && value.length >= 10) {
        const d = new Date(value);
        return !isNaN(d.getTime());
    }
    return false;
}

function toEncoreTime(value: string | Date): string {
    const date = typeof value === 'string' ? new Date(value) : value;
    return date.toISOString();
}

/**
 * Cleans an object for Apollo variables:
 *  - removes "", null, undefined
 *  - converts date-like values to ISO string
 *  - removes empty objects/arrays
 */
export function cleanVariables<T extends OperationVariables>(obj: T): OperationVariables {
    const out: OperationVariables = {};

    for (const [k, v] of Object.entries(obj)) {
        if (v === null || v === undefined) continue;
        if (typeof v === 'string' && v.trim() === '') continue;

        if (isDateLike(v)) {
            out[k] = toEncoreTime(v as string | Date);
            continue;
        }

        if (Array.isArray(v)) {
            const cleaned = v.map((item) => (typeof item === 'object' ? cleanVariables(item as OperationVariables) : item)).filter((item) => item !== null && item !== undefined && item !== '');
            if (cleaned.length > 0) {
                out[k] = cleaned;
            }
            continue;
        }

        if (typeof v === 'object') {
            const cleaned = cleanVariables(v as OperationVariables);
            if (Object.keys(cleaned).length > 0) {
                out[k] = cleaned;
            }
            continue;
        }

        out[k] = v;
    }

    return out;
}